import { useEffect } from "react";

/**
 * useOgMeta — updates Open Graph + Twitter meta tags imperatively.
 *
 * Social scrapers that execute JS (LinkedIn, Slack, Discord, some of
 * Twitter's renderers) will pick up overrides set via direct DOM mutation,
 * which is why we don't use a helmet library for this tiny need.
 *
 * For scrapers that DON'T execute JS (Facebook, iMessage), the base tags
 * in `public/index.html` already unfurl with the same Elementary Pilot
 * branding — so behavior degrades gracefully.
 */
export function useOgMeta({ title, description, image, url } = {}) {
  useEffect(() => {
    const set = (selector, attr, value) => {
      if (!value) return undefined;
      let el = document.head.querySelector(selector);
      const created = !el;
      if (created) {
        el = document.createElement("meta");
        const [propKey, propVal] = selector
          .replace(/^meta\[/, "")
          .replace(/\]$/, "")
          .split("=");
        el.setAttribute(propKey, propVal.replace(/['"]/g, ""));
        document.head.appendChild(el);
      }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return () => {
        if (created) {
          document.head.removeChild(el);
        } else if (prev != null) {
          el.setAttribute(attr, prev);
        }
      };
    };

    const cleanups = [
      title && (() => {
        const prev = document.title;
        document.title = title;
        return () => {
          document.title = prev;
        };
      })(),
      set('meta[property="og:title"]', "content", title),
      set('meta[property="og:description"]', "content", description),
      set('meta[property="og:image"]', "content", image),
      set('meta[property="og:url"]', "content", url || (typeof window !== "undefined" ? window.location.href : undefined)),
      set('meta[name="twitter:title"]', "content", title),
      set('meta[name="twitter:description"]', "content", description),
      set('meta[name="twitter:image"]', "content", image),
      set('meta[name="description"]', "content", description),
    ].filter(Boolean);

    return () => cleanups.forEach((fn) => fn && fn());
  }, [title, description, image, url]);
}
